import { useEffect, useState } from 'react';
import type { AttendanceOverviewDto } from '@ciimar/shared';
import { api } from '../api/client';
import { useT } from '../lib/i18n';
import { Icon } from '../portal/primitives';

/**
 * Registo Presenças (attendance). Structure is in place; it reads the daily
 * records from an internal API or SQL database once linked. Until the match id
 * (webapp ↔ records DB) is configured, it shows the integration state and a
 * clear "prepared, awaiting link" placeholder instead of records.
 */
export function Attendance() {
  const t = useT();
  const [data, setData] = useState<AttendanceOverviewDto | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    api
      .attendanceOverview()
      .then(setData)
      .catch((err) => setError((err as Error).message));
  }, []);

  const status = data?.status;
  const records = data?.records ?? [];

  return (
    <div className="att-page">
      <div className="att-head">
        <div>
          <h2 className="page-title">{t('attendance.title')}</h2>
          <p className="page-sub">{t('attendance.subtitle')}</p>
        </div>
        {status && (
          <span className={`att-chip ${status.linked ? 'on' : status.configured ? 'warn' : ''}`}>
            {status.linked
              ? t('attendance.chip.linked')
              : status.configured
                ? t('attendance.chip.configured')
                : t('attendance.chip.off')}
          </span>
        )}
      </div>

      {error && <div className="alert error">{error}</div>}

      {status && !status.linked && (
        <div className="att-notice">
          <Icon name="cal" size={22} />
          <div>
            <strong>{t('attendance.prepared.title')}</strong>
            <p>{t('attendance.prepared.body')}</p>
            {status.message && <p className="muted att-state-msg">{status.message}</p>}
          </div>
        </div>
      )}

      <div className="erp-panel att-records">
        <div className="erp-toolbar">
          <strong>{t('attendance.records.title')}</strong>
        </div>
        {records.length === 0 ? (
          <div className="panel-empty is-empty">{t('attendance.records.empty')}</div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table className="grid">
              <thead>
                <tr>
                  <th>{t('attendance.table.date')}</th>
                  <th>{t('attendance.table.checkIn')}</th>
                  <th>{t('attendance.table.checkOut')}</th>
                  <th>{t('attendance.table.status')}</th>
                </tr>
              </thead>
              <tbody>
                {records.map((r) => (
                  <tr key={r.date}>
                    <td>{r.date}</td>
                    <td>{r.checkIn ?? '—'}</td>
                    <td>{r.checkOut ?? '—'}</td>
                    <td>{r.status ?? '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
